import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/lib/utils";

interface AppHeaderRailProps {
  eyebrow?: string;
  /**
   * Heading content. Callers own their own typography (size, weight,
   * `truncate`) — typically an `<h1>` — so the rail renders it verbatim.
   */
  title: ReactNode;
  description?: ReactNode;
  leading?: ReactNode;
  contextPill?: ReactNode;
  actions?: ReactNode;
  /** Full-width content rendered below the main row (e.g. error banners). */
  below?: ReactNode;
  /**
   * Tightens the row layout (less vertical padding, single non-wrapping row)
   * for thin top bars. Layout-only — it does not change title typography,
   * which callers own.
   */
  compact?: boolean;
  className?: string;
}

/**
 * The sole control for opening/closing the sidebar. On desktop it collapses
 * the sticky sidebar to its icon rail; on mobile it is the *only* entrypoint
 * to reveal the off-canvas sidebar. It is therefore intentionally rendered at
 * all breakpoints (no `md:hidden`).
 */
function SidebarRailToggle() {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const Icon = sidebarOpen ? PanelLeftClose : PanelLeftOpen;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
      aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
      aria-expanded={sidebarOpen}
      aria-controls="app-sidebar"
      onClick={toggleSidebar}
    >
      <Icon className="size-4" />
    </Button>
  );
}

/**
 * Shared top app chrome used by every authenticated surface (page headers and
 * workspace top bars). It anchors the sidebar collapse/expand toggle at the
 * left edge of the content area.
 *
 * IMPORTANT: the embedded {@link SidebarRailToggle} is the sole mobile
 * entrypoint for opening the sidebar, so an `AppHeaderRail` MUST be present on
 * every authenticated page. A page that renders without one (e.g. a bare
 * loading skeleton) would strand mobile users with no way to open navigation.
 */
export function AppHeaderRail({
  eyebrow,
  title,
  description,
  leading,
  contextPill,
  actions,
  below,
  compact = false,
  className,
}: AppHeaderRailProps) {
  return (
    <header className={cn("border-b bg-background", className)}>
      <div className={cn("flex min-h-12 items-center gap-3 px-4 md:px-6", compact ? "py-2" : "flex-wrap py-3")}>
        <SidebarRailToggle />
        {leading ? <div className="flex shrink-0 items-center">{leading}</div> : null}
        {contextPill ? <div className="shrink-0">{contextPill}</div> : null}
        <div className="min-w-0 flex-1">
          {eyebrow ? <p className="text-xs font-semibold uppercase text-muted-foreground">{eyebrow}</p> : null}
          {title}
          {description ? <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">{actions}</div> : null}
      </div>
      {below ? <div className="px-4 pb-3 md:px-6">{below}</div> : null}
    </header>
  );
}
